import {
  type AortaWorksheetData,
  type BladderWorksheetData,
  type CardiacWorksheetData,
  type EFastWorksheetData,
  type ExamWorksheetData,
  isProcedureWorksheet,
  isUsWorksheet,
  type LungWorksheetData,
  MSK_REGIONS,
  type MskRegion,
  type MskWorksheetData,
  type PregnancyWorksheetData,
  type ProcedureWorksheetData,
  type RenalWorksheetData,
  type UsWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  describeMskRegionFindings,
  MSK_REGION_CONFIG,
  mskRegionData,
  mskViewKey,
  scannedMskRegions,
} from "../../../_lib/worksheets/msk-regions";
import {
  PROCEDURE_DEFINITIONS,
  procedureOptionLabel,
  procedureViewLabels,
} from "../../../_lib/worksheets/procedures/procedure-definitions";
import {
  summarizeUs,
  usViewLabels,
} from "../../../_lib/worksheets/us/us-definitions";

const MSK_VIEW_LABELS: Record<string, string> = Object.fromEntries(
  MSK_REGIONS.flatMap((region) =>
    MSK_REGION_CONFIG[region].views.map((view) => [
      mskViewKey(region, view.id),
      `${MSK_REGION_CONFIG[region].label}: ${view.label}`,
    ]),
  ),
);

export const VIEW_LABELS: Record<string, string> = {
  ruq: "RUQ",
  luq: "LUQ",
  suprapubic: "Suprapubic",
  subxiphoid: "Subxiphoid",
  rLung: "R Lung",
  lLung: "L Lung",
  plax: "PLAX",
  psax: "PSAX",
  apical4Chamber: "A4C",
  rUpperAnterior: "R Upper Ant",
  rLowerAnterior: "R Lower Ant",
  rPosterior: "R Post",
  lUpperAnterior: "L Upper Ant",
  lLowerAnterior: "L Lower Ant",
  lPosterior: "L Post",
  sagittal: "Sagittal",
  transverse: "Transverse",
  rKidneyLong: "R Long",
  rKidneyTrans: "R Trans",
  lKidneyLong: "L Long",
  lKidneyTrans: "L Trans",
  bladder: "Bladder",
  proximalTrans: "Prox Trans",
  proximalSag: "Prox Sag",
  midTrans: "Mid Trans",
  midSag: "Mid Sag",
  distalTrans: "Dist Trans",
  distalSag: "Dist Sag",
  transabdominalSagittal: "TA Sag",
  transabdominalTransverse: "TA Trans",
  transvaginalSagittal: "TV Sag",
  transvaginalTransverse: "TV Trans",
  ...MSK_VIEW_LABELS,
};

export const formatIndication = (indication: string | null): string => {
  if (!indication) return "";
  return indication.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getCompletedViews = (views: Record<string, boolean>): string[] =>
  Object.entries(views)
    .filter(([, v]) => v)
    .map(([k]) => VIEW_LABELS[k] ?? k);

export const completedViewLabels = (data: ExamWorksheetData): string[] => {
  if (isProcedureWorksheet(data)) return procedureViewLabels(data);
  if (isUsWorksheet(data)) return usViewLabels(data);
  return getCompletedViews(data.views as Record<string, boolean>);
};

const summarizeEFast = (data: EFastWorksheetData): string => {
  const positive = Object.entries(data.findings)
    .filter(([, v]) => v === "present")
    .map(([k]) =>
      k
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()
        .trim(),
    );
  return positive.length > 0 ? positive.join(", ") : "No positive findings";
};

const summarizeCardiac = (data: CardiacWorksheetData): string => {
  const items: string[] = [];
  if (data.findings.lvFunction)
    items.push(`LV: ${data.findings.lvFunction.replace(/_/g, " ")}`);
  if (
    data.findings.pericardialEffusion &&
    data.findings.pericardialEffusion !== "none"
  )
    items.push(`Pericardial effusion: ${data.findings.pericardialEffusion}`);
  if (data.findings.rvDilatation) items.push("RV dilatation");
  return items.length > 0 ? items.join(", ") : "No significant findings";
};

const summarizeLung = (data: LungWorksheetData): string => {
  const abnormal: string[] = [];
  for (const [zone, findings] of Object.entries(data.zones)) {
    if (findings.lungSliding === false) abnormal.push(`${zone}: no sliding`);
    if (findings.bLines === "3+" || findings.bLines === "confluent")
      abnormal.push(`${zone}: B-lines`);
    if (findings.pleuralEffusion && findings.pleuralEffusion !== "none")
      abnormal.push(`${zone}: effusion`);
  }
  return abnormal.length > 0 ? abnormal.join(", ") : "Normal lung findings";
};

const summarizeBladder = (data: BladderWorksheetData): string => {
  const items: string[] = [];
  if (data.findings.bladderVolumeMl !== null)
    items.push(`Vol: ${data.findings.bladderVolumeMl}mL`);
  if (data.findings.distension && data.findings.distension !== "normal")
    items.push(`Distension: ${data.findings.distension}`);
  return items.length > 0 ? items.join(", ") : "Normal bladder";
};

const summarizeRenal = (data: RenalWorksheetData): string => {
  const items: string[] = [];
  for (const [label, findings] of [
    ["R kidney", data.findings.rKidney],
    ["L kidney", data.findings.lKidney],
  ] as const) {
    if (findings.hydronephrosis && findings.hydronephrosis !== "none")
      items.push(`${label}: hydronephrosis (${findings.hydronephrosis})`);
    if (findings.stones) items.push(`${label}: stones`);
  }
  return items.length > 0 ? items.join(", ") : "No renal abnormalities";
};

const summarizeAorta = (data: AortaWorksheetData): string => {
  const items: string[] = [];
  for (const [label, findings] of [
    ["Proximal", data.findings.proximal],
    ["Mid", data.findings.mid],
    ["Distal", data.findings.distal],
  ] as const) {
    if (findings.aneurysm) items.push(`${label}: aneurysm`);
    else if (findings.diameterCm !== null && findings.diameterCm > 3)
      items.push(`${label}: ${findings.diameterCm}cm`);
  }
  return items.length > 0 ? items.join(", ") : "Normal aorta";
};

const summarizePregnancy = (data: PregnancyWorksheetData): string => {
  const items: string[] = [];
  const f = data.findings;
  if (f.intrauterineGestation === false) items.push("No IUP visualised");
  else if (f.intrauterineGestation) items.push("IUP visualised");
  if (f.cardiacActivity === "absent") items.push("no cardiac activity");
  else if (f.cardiacActivity === "present" && f.fetalHeartRateBpm !== null)
    items.push(`FHR ${f.fetalHeartRateBpm} bpm`);
  if (f.numberOfGestations !== null && f.numberOfGestations > 1)
    items.push(`${f.numberOfGestations} gestations`);
  if (f.estimatedGestationalAgeWeeks !== null)
    items.push(`EGA ${f.estimatedGestationalAgeWeeks} wk`);
  if (f.estimatedDueDate) {
    // EDD is a date-only string. Parse as UTC midnight AND format with
    // timeZone: "UTC" so viewers in negative offsets don't see the previous
    // calendar day.
    const edd = new Date(`${f.estimatedDueDate}T00:00:00Z`);
    const formatted = Number.isNaN(edd.getTime())
      ? f.estimatedDueDate
      : edd.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        });
    items.push(`EDD ${formatted}`);
  }
  if (f.estimatedFetalWeightG !== null)
    items.push(`EFW ${f.estimatedFetalWeightG} g`);
  if (f.fetalPresentation && f.fetalPresentation !== "cephalic")
    items.push(`${f.fetalPresentation} presentation`);
  if (f.uterineFibroidsSeen) items.push("fibroids");
  if (f.pelvicFreeFluid && f.pelvicFreeFluid !== "none")
    items.push(`pelvic free fluid: ${f.pelvicFreeFluid}`);
  return items.length > 0 ? items.join(", ") : "No significant findings";
};

const summarizeMskRegion = (
  data: MskWorksheetData,
  region: MskRegion,
): string => {
  const { side } = mskRegionData(data, region);
  const label = side
    ? `${MSK_REGION_CONFIG[region].label} (${side})`
    : MSK_REGION_CONFIG[region].label;
  const items = describeMskRegionFindings(data, region);
  return `${label}: ${items.length > 0 ? items.join(", ") : "no abnormality"}`;
};

const summarizeMsk = (data: MskWorksheetData): string => {
  const regions = scannedMskRegions(data);
  return regions.length > 0
    ? regions.map((region) => summarizeMskRegion(data, region)).join("; ")
    : "No regions documented";
};

const summarizeProcedure = (data: ProcedureWorksheetData): string => {
  const definition = PROCEDURE_DEFINITIONS[data.examType];
  const line = (label: string, fieldId: string) => {
    const value = procedureOptionLabel(data, fieldId);
    return value ? [`${label}: ${value}`] : [];
  };
  const items = [
    ...line("Procedure", "procedure_type"),
    ...line(
      definition.siteFieldId === "nerve" ? "Nerve" : "Site",
      definition.siteFieldId,
    ),
    ...line("Side", "side"),
    ...line("Needle tip visualized", definition.needleTipFieldId),
    ...line("Needle-in-target image", "needle_in_target_image_saved"),
    ...line("Complications", "immediate_complications"),
  ];
  return items.length > 0 ? items.join(", ") : "No procedure details recorded";
};

const summarizeUsWorksheet = (data: UsWorksheetData): string => {
  const { abnormal, impression } = summarizeUs(data);
  const findings =
    abnormal.length > 0 ? abnormal.join(", ") : "No abnormal findings";
  return impression ? `${findings}\nImpression: ${impression}` : findings;
};

export const summarizeWorksheet = (data: ExamWorksheetData): string => {
  if (isUsWorksheet(data)) return summarizeUsWorksheet(data);
  switch (data.examType) {
    case "efast":
      return summarizeEFast(data);
    case "cardiac":
      return summarizeCardiac(data);
    case "lung":
      return summarizeLung(data);
    case "bladder":
      return summarizeBladder(data);
    case "renal":
      return summarizeRenal(data);
    case "aorta":
      return summarizeAorta(data);
    case "pregnancy":
      return summarizePregnancy(data);
    case "msk":
      return summarizeMsk(data);
    case "guided_joint_injection":
    case "guided_tendon_injection":
    case "guided_nerve_block":
      return summarizeProcedure(data);
    default:
      return "No summary available";
  }
};
